
import React from "react";
import * as am4charts from '@amcharts/amcharts4/charts';
import { getStackedColumnsDateChartAxisTooltipAdapter, getStackedColumnsDateChartTooltipAdapter } from "./tooltips";
import { BaseStackedColumnsChartProps, StackedColumnsChart } from "./stackedColumnsChart";
import { createYAxis } from "./lib";

export interface StackedColumnsCategoryChartProps<Data extends Record<string, any>> extends
  BaseStackedColumnsChartProps<Data>
{
  tooltipDateKey?: keyof Data,
  formatTooltipDate?: (date: number | string) => string,
  totalDescription?: string,
  formatData?: (dataForSeries: any, allData?: Data) => string,
  amchartsYAxisNumberFormat?: string,
  onlyShowCurrentSeriesInTooltip?: boolean,
  categoryKey: keyof Data
}

export function StackedColumnCategoryChart<Data extends { date: string }>(props: StackedColumnsCategoryChartProps<Data>) {

  const getTooltipAdapter = props.getTooltipAdapter ?? (({ seriesName }: { seriesName: string }) =>
    getStackedColumnsDateChartTooltipAdapter({
      seriesName,
      totalDescription: props.totalDescription,
      formatData: props.formatData,
      amchartsNumberFormat: props.amchartsYAxisNumberFormat,
      onlyShowCurrentSeries: props.onlyShowCurrentSeriesInTooltip,
      dateKey: props.tooltipDateKey,
      formatTooltipDate: props.formatTooltipDate
    }));

  const getAxisTooltipAdapter = props.getAxisTooltipAdapter ?? (() =>
    getStackedColumnsDateChartAxisTooltipAdapter({
      totalDescription: props.totalDescription,
      formatData: props.formatData,
      amchartsNumberFormat: props.amchartsYAxisNumberFormat,
      dateKey: props.tooltipDateKey,
      formatTooltipDate: props.formatTooltipDate
    }));

  const provideXAxis = (chart: am4charts.XYChart) : am4charts.CategoryAxis => {
    const xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    xAxis.dataFields.category = props.categoryKey as string;
    xAxis.renderer.grid.template.disabled = true;
    xAxis.renderer.minGridDistance = 50;

    if (!props.useAxisTooltip) {
      xAxis.cursorTooltipEnabled = false;
    }
    if (props.useAxisTooltip) {
      xAxis.dummyData = { allSeries: props.series };
      xAxis.adapter.add('getTooltipText', getAxisTooltipAdapter());
      xAxis.dummyData.allSeries = props.series;

      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.disabled = true;
      chart.cursor.lineX.disabled = true;
      chart.cursor.maxTooltipDistance = -1;
      chart.cursor.behavior = 'none';
    }

    return xAxis;
  };
  const provideYAxis = createYAxis(props.yAxisType, props.useAxisTooltip);
  return (
    <StackedColumnsChart<Data>
      {...props}
      provideXAxis={provideXAxis}
      provideYAxis={provideYAxis}
      getTooltipAdapter={getTooltipAdapter}
      xAxisType="category"
      xFieldType="categoryX"
      categoryKey={props.categoryKey}
    />
  );
}
